/* eslint-disable react/destructuring-assignment */
import React, { useId } from 'react'
import { Box, Button, ButtonProps, Menu, MenuItem, SxProps, Typography } from '@mui/material'
import clsx from 'clsx'
import LockIcon from '@mui/icons-material/Lock'
import { makeStyles } from '@mui/styles'
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded'
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded'
import useStyles from './_styles'
import { ButtonSelectorProps } from './_props'
import { generateRandomGuiID } from '../../../utils/commonUtils'
import WarningMessage from '../WarningMessage'
import { defaultPalette } from '../../../theme/idsTheme'

const ButtonSelector = <T,>({
    label,
    placeholder = 'Choisir',
    options = [],
    warningMessage,
    onClose,
    disabled,
    dataCy,
    locked = false,
    menuTitle,
    ...props
}: ButtonSelectorProps<T>) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const classes = useStyles()
    const open = Boolean(anchorEl)

    const boundingClientRect = anchorEl?.getBoundingClientRect()

    const left = boundingClientRect?.left
    const width = boundingClientRect?.width

    const computedWidth = width ? `${width - 20}px !important` : undefined
    const computedLeft = left ? `${left + 10}px !important` : undefined

    const useComputedStyles = makeStyles(() => ({
        ButtonSelectorPaper: {
            width: computedWidth,
            left: computedLeft,
        },
    }))

    const computedClasses = useComputedStyles()
    const id = useId()
    const handleMenuOpen: ButtonProps['onClick'] = (event) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
        if (onClose && props.type === 'default') onClose()
    }

    const getIsSelected = (option: T) => {
        if (Array.isArray(props.value)) {
            return Boolean(props.value?.find((x) => JSON.stringify(option) === JSON.stringify(x)))
        }
        return JSON.stringify(option) === JSON.stringify(props.value)
    }

    const renderLabel = () => {
        if (!props.value) {
            return placeholder ?? '--'
        }

        if (props.renderOption && props.value) {
            if (props.type === 'default') {
                return props.renderOption(props.value)
            }
            if (props.type === 'multiple') {
                return (
                    <>
                        {props.value.map((x) => {
                            if (props.renderOption) {
                                return props.renderOption(x)
                            }
                            return null
                        })}
                    </>
                )
            }
        }

        if (label) {
            return label
        }

        return placeholder ?? '--'
    }

    const computedLabel = renderLabel()

    const icon = React.useMemo(() => {
        if (locked) {
            return (
                <LockIcon
                    sx={{
                        width: '1.3rem',
                        height: '1.3rem',
                        opacity: 0.7,
                    }}
                />
            )
        }

        const iconSx: SxProps = {
            transform: 'scale(1.4)',
        }

        if (open) {
            return <ArrowDropUpRoundedIcon sx={iconSx} />
        }

        return <ArrowDropDownRoundedIcon sx={iconSx} />
    }, [locked, open])

    return (
        <>
            {options.length > 0 && anchorEl && !disabled && (
                <Menu
                    classes={{
                        paper: clsx(classes.ButtonSelectorPaper, computedClasses.ButtonSelectorPaper),
                    }}
                    id={`ButtonSelector-menu-${id}`}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        'aria-labelledby': `ButtonSelector-button-${id}`,
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    {menuTitle && (
                        <Typography
                            sx={{
                                padding: '.5rem',
                                opacity: 0.7,
                                fontSize: '.9rem',
                                borderBottom: `1px solid ${defaultPalette.lightGrey.half}`,
                            }}
                        >
                            {menuTitle}
                        </Typography>
                    )}
                    <Box
                        className={classes.ButtonSelectorPaperInner}
                        sx={{
                            maxHeight: '25rem',
                            overflow: 'auto',
                        }}
                    >
                        {options.map((option) => {
                            const isSelected = getIsSelected(option.value)
                            const key = generateRandomGuiID()

                            return (
                                <MenuItem
                                    key={key}
                                    className={clsx(
                                        classes.ButtonSelectorMenuItem,
                                        isSelected && classes.ButtonSelectorMenuItemSelected,
                                    )}
                                    disabled={props.type === 'default' && isSelected}
                                    onClick={() => {
                                        if (props.type === 'multiple') {
                                            if (isSelected) {
                                                const res = props.value?.filter(
                                                    (x) => JSON.stringify(x) !== JSON.stringify(option.value),
                                                )

                                                props.onChange(res)
                                                return
                                            }

                                            props.onChange([...(props.value ?? []), option.value])
                                            return
                                        }

                                        if (!isSelected) {
                                            props.onChange(option.value)
                                            handleClose()
                                        }
                                    }}
                                >
                                    {props.renderOption ? props.renderOption(option.value) : option.label}
                                </MenuItem>
                            )
                        })}
                    </Box>
                </Menu>
            )}
            <div className={classes.ButtonSelectorContainer}>
                <Button
                    variant="outlined"
                    color="inherit"
                    className={clsx(classes.ButtonSelector, 'TTD-ButtonSelector-button')}
                    id={`ButtonSelector-button-${id}`}
                    aria-controls={open ? `ButtonSelector-menu-${id}` : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleMenuOpen}
                    disabled={disabled}
                    data-cy={dataCy}
                >
                    <span
                        className={clsx(classes.ButtonSelectorLabel, !label && classes.ButtonSelectorLabelPlaceholder)}
                    >
                        {computedLabel}
                    </span>
                    {icon}
                </Button>
                {warningMessage && <WarningMessage message={warningMessage} />}
            </div>
        </>
    )
}

export default ButtonSelector
